import React, { useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  TruckIcon,
  CalendarDaysIcon,
  BuildingOffice2Icon,
  UsersIcon,
  ClockIcon,
  PlusCircleIcon,
  Cog6ToothIcon,
  HomeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { PageTitle } from '../../components/ui';
import { StatCard } from '../../components/ui/StatCard';
import { QuickActionCard } from '../../components/ui/QuickActionCard';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthorization } from '../../hooks/useAuthorization';
import { Role } from '../../types';
import { useMasterData } from '../../hooks/useMasterData';
import logger from '../../utils/logger';
import { isValidDate } from '../../constants/dates';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { DemoGuidance } from '../../components/demo/DemoGuidance';
import { DriverScheduleView } from '../../components/schedule/DriverScheduleView';
import { SimplifiedCaseCards } from '../../components/dashboard/SimplifiedCaseCards';
import { ScheduleSummary } from '../../components/dashboard/ScheduleSummary';
import { ManagementCaseDashboard } from '../../components/dashboard/ManagementCaseDashboard';
import { DriverTimeSlotView } from '../../components/schedule/DriverTimeSlotView';

const DashboardPage: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkPermission } = useAuthorization();

  // Hooks must be called first, before any conditional returns
  const {
    cases,
    landTransporters,
    businessPartners,
    getCompanyPartnerSelectionsByCompanyId,
    // getVehicleById,
  } = useMasterData();

  // Case helper functions for displaying case information (removed unused)

  // 案件詳細ポップアップの状態（現在未使用）
  // const [selectedCase, setSelectedCase] = useState<Case | null>(null);

  // 今日の日付を取得（VITE_FIXED_DATEまたは今日の日付）
  const today = useMemo(() => {
    const fixedDate = import.meta.env.VITE_FIXED_DATE;
    return fixedDate && isValidDate(fixedDate) ? fixedDate : new Date().toISOString().split('T')[0];
  }, []);

  // 会社の取引先選択設定を取得
  const companyPartnerSelections = useMemo(() => {
    return user?.companyId ? getCompanyPartnerSelectionsByCompanyId(user.companyId) : [];
  }, [user?.companyId, getCompanyPartnerSelectionsByCompanyId]);

  // 今日の案件を取得（引取時間順にソート）
  const todaysCases = useMemo(() => {
    return cases
      .filter(c => c.pickupDate === today)
      .sort((a, b) => a.pickupTimeStart.localeCompare(b.pickupTimeStart));
  }, [cases, today]);

  // ロール別の案件フィルタリング
  const filteredTodaysCases = useMemo(() => {
    if (user?.role === Role.DRIVER && user?.landTransporterId) {
      // 陸送員は自分に割り当てられた案件のみ
      return todaysCases.filter(c => c.assignedLandTransporterId === user.landTransporterId);
    }
    return todaysCases;
  }, [todaysCases, user]);

  // Stats calculation
  const stats = useMemo(() => {
    const ongoingCases = cases.filter(
      c => c.status === 'assigned' || c.status === 'in_transit'
    ).length;

    // データロード状態の確認
    const isDataLoaded = !loading && businessPartners.length >= 0 && landTransporters.length >= 0;

    if (process.env.NODE_ENV === 'development') {
      logger.debug('🔍 Dashboard Stats - Data State Check:', {
        loading,
        isDataLoaded,
        businessPartnersLength: businessPartners.length,
        landTransportersLength: landTransporters.length,
        casesLength: cases.length,
        companyPartnerSelectionsLength: companyPartnerSelections.length,
      });
    }

    // 有効な取引先の計算（統一ロジック実装）
    let totalActivePartners = 0;
    if (isDataLoaded) {
      // 有効な取引先を統一ロジックで計算
      const enabledPartners = businessPartners.filter(bp => {
        const selection = companyPartnerSelections.find(s => s.businessPartnerId === bp.id);

        // customタイプもrakuhai_providedタイプも同じロジックで判定
        // company_partner_selectionsでis_enabled=trueの場合のみ有効とみなす
        return selection?.isEnabled === true;
      });

      totalActivePartners = enabledPartners.length;

      if (process.env.NODE_ENV === 'development') {
        // デバッグ用の詳細分析
        const customPartners = enabledPartners.filter(bp => bp.dataSource === 'custom');
        const rakuhaiPartners = enabledPartners.filter(bp => bp.dataSource === 'rakuhai_provided');

        logger.debug('📊 Active Partners Calculation Detail:', {
          enabledPartners: enabledPartners.length,
          customPartners: customPartners.length,
          customPartnersNames: customPartners.map(bp => bp.name),
          rakuhaiPartners: rakuhaiPartners.length,
          rakuhaiPartnersNames: rakuhaiPartners.map(bp => bp.name),
          totalActivePartners,
          allBusinessPartners: businessPartners.length,
          companyPartnerSelections: companyPartnerSelections.map(s => ({
            businessPartnerId: s.businessPartnerId,
            isEnabled: s.isEnabled,
          })),
        });
      }
    }

    const totalDrivers = isDataLoaded ? landTransporters.length : 0;
    const pendingDispatch = cases.filter(c => !c.assignedLandTransporterId).length;

    const result = {
      ongoingCases,
      activeClients: totalActivePartners,
      availableDrivers: totalDrivers,
      pendingDispatch,
    };

    if (process.env.NODE_ENV === 'development') {
      logger.debug('✅ Dashboard Stats Final Result:', {
        ...result,
        calculationTime: new Date().toISOString(),
        dataLoadedState: isDataLoaded,
      });
    }

    return result;
  }, [loading, cases, businessPartners, landTransporters, companyPartnerSelections]);

  // データ変更の監視とデバッグ
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      logger.debug('📈 Dashboard Data Change Detected:', {
        timestamp: new Date().toISOString(),
        loading: loading,
        dataState: {
          businessPartners: businessPartners.length,
          landTransporters: landTransporters.length,
          cases: cases.length,
          companyPartnerSelections: companyPartnerSelections.length,
        },
        businessPartnersDetail:
          businessPartners.length > 0
            ? {
                sampleNames: businessPartners.slice(0, 2).map(bp => bp.name),
                dataSources: businessPartners.map(bp => ({
                  name: bp.name,
                  dataSource: bp.dataSource,
                })),
              }
            : 'empty',
        landTransportersDetail:
          landTransporters.length > 0
            ? {
                sampleNames: landTransporters.slice(0, 2).map(lt => lt.name),
                statuses: landTransporters.map(lt => lt.status),
              }
            : 'empty',
        calculatedStats: {
          activeClients: stats.activeClients,
          availableDrivers: stats.availableDrivers,
          ongoingCases: stats.ongoingCases,
          pendingDispatch: stats.pendingDispatch,
        },
      });
    }
  }, [loading, businessPartners, landTransporters, cases, companyPartnerSelections, stats]);

  const loadAdditionalData = useCallback(() => {
    // 追加データ読み込みのロジック（必要に応じて実装）
    logger.debug('📊 追加データ読み込み処理');
  }, []);

  const handleCloseDemoGuidance = useCallback(() => {
    // Demo guidance closed
  }, []);

  const handleStartScenario = useCallback(
    (scenario: string) => {
      logger.info(`🎯 デモシナリオ開始: ${scenario}`);
      // Demo guidance will be closed

      // シナリオに応じたページ遷移
      switch (scenario) {
        case 'dispatch':
          navigate('/cases');
          break;
        case 'schedule':
          navigate('/schedule');
          break;
        case 'manage-partners':
          navigate('/settings');
          break;
        default:
          logger.warn('⚠️ 不明なシナリオ:', scenario);
      }
    },
    [navigate]
  );

  useEffect(() => {
    loadAdditionalData();
  }, [loadAdditionalData]);

  useEffect(() => {
    logger.info('🔍 DashboardPage useEffect:', {
      pathname: location.pathname,
      user: user ? { name: user.name, role: user.role } : null,
      loading,
      shouldRedirect:
        user && user.role === Role.SUPER_ADMIN && location.pathname !== '/admin/super',
    });

    // ローディング中はリダイレクトしない
    if (loading) {
      logger.info('⏳ ローディング中のためリダイレクト待機');
      return;
    }

    // super_adminユーザーはSuperAdminDashboardにリダイレクト
    // ただし、既に/admin/superにいる場合はリダイレクトしない
    if (user && user.role === Role.SUPER_ADMIN && location.pathname !== '/admin/super') {
      logger.info('🔄 super_adminユーザーをSuperAdminDashboardにリダイレクト');
      navigate('/admin/super', { replace: true });
      return; // リダイレクト後は処理を終了
    }
  }, [user, loading, location.pathname, navigate]);

  // ローディング中の表示
  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4'></div>
          <p className='text-gray-600 dark:text-gray-300'>読み込み中...</p>
        </div>
      </div>
    );
  }

  // 権限チェック（ローディング完了後）
  if (!checkPermission([Role.ADMIN, Role.DISPATCHER, Role.DRIVER, Role.MANAGER, Role.READ_ONLY])) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-600 dark:text-red-400'>アクセス権限がありません。</p>
        </div>
      </div>
    );
  }

  // ユーザー情報がない場合の処理
  if (!user) {
    logger.warn('⚠️ ユーザー情報が取得できません');
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-red-600 dark:text-red-400'>ユーザー情報が取得できません。</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      <PageTitle
        icon={HomeIcon}
        title={
          user.role === Role.DRIVER
            ? '陸送員ダッシュボード'
            : user.role === Role.ADMIN
              ? '管理者ダッシュボード'
              : user.role === Role.DISPATCHER
                ? '配車担当ダッシュボード'
                : 'ダッシュボード'
        }
      />

      <div className='max-w-7xl mx-auto py-6 sm:px-6 lg:px-8'>
        <div className='px-4 py-6 sm:px-0'>
          {/* Stats Grid - 陸送員以外に表示 */}
          {user.role !== Role.DRIVER && (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
              <StatCard
                title='進行中案件'
                value={stats.ongoingCases.toString()}
                icon={<TruckIcon className='h-6 w-6 text-blue-600 dark:text-blue-400' />}
                color='blue'
              />
              <StatCard
                title='有効な取引先'
                value={stats.activeClients.toString()}
                icon={
                  <BuildingOffice2Icon className='h-6 w-6 text-green-600 dark:text-green-400' />
                }
                color='green'
              />
              <StatCard
                title='陸送員'
                value={stats.availableDrivers.toString()}
                icon={<UsersIcon className='h-6 w-6 text-orange-600 dark:text-orange-400' />}
                color='orange'
              />
              <StatCard
                title='配車待ち'
                value={stats.pendingDispatch.toString()}
                icon={<ClockIcon className='h-6 w-6 text-purple-600 dark:text-purple-400' />}
                color='purple'
              />
            </div>
          )}

          {/* Schedule for read-only users only */}
          {user.role === Role.READ_ONLY && (
            <div className='mb-8'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                本日のスケジュール ({formatDateForDisplay(today)})
              </h3>
              <DriverScheduleView selectedDate={today} viewMode='admin' />
            </div>
          )}

          {/* Admin/Dispatcher Quick Actions */}
          {(user.role === Role.ADMIN || user.role === Role.DISPATCHER) && (
            <div className='mb-8'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                クイックアクション
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                <QuickActionCard
                  title='案件管理'
                  description='新規案件の作成と既存案件の管理'
                  icon={<PlusCircleIcon className='h-6 w-6 text-blue-600' />}
                  onClick={() => navigate('/cases')}
                  color='blue'
                />
                <QuickActionCard
                  title='陸送員スケジュール'
                  description='陸送員の配車スケジュール管理'
                  icon={<CalendarDaysIcon className='h-6 w-6 text-green-600' />}
                  onClick={() => navigate('/schedule')}
                  color='green'
                />
                {user.role === Role.ADMIN && (
                  <QuickActionCard
                    title='設定管理'
                    description='システム設定とマスタデータ管理'
                    icon={<Cog6ToothIcon className='h-6 w-6 text-purple-600' />}
                    onClick={() => navigate('/settings')}
                    color='purple'
                  />
                )}
              </div>
            </div>
          )}

          {/* ロール別案件表示 */}
          {user?.role === Role.DRIVER && (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8'>
              {/* タイムスロットスケジュール */}
              <div>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                  本日のスケジュール ({formatDateForDisplay(today)})
                </h3>
                <DriverTimeSlotView
                  cases={filteredTodaysCases}
                  currentTime={new Date().toLocaleTimeString('ja-JP', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                />
              </div>

              {/* シンプル案件カード */}
              <div>
                <SimplifiedCaseCards cases={filteredTodaysCases} selectedDate={today} />
              </div>
            </div>
          )}

          {user?.role === Role.READ_ONLY && (
            <ScheduleSummary cases={todaysCases} selectedDate={today} />
          )}

          {/* 管理者・配車担当向けの改善された案件管理ダッシュボード */}
          {(user?.role === Role.ADMIN || user?.role === Role.DISPATCHER) && (
            <ManagementCaseDashboard cases={todaysCases} selectedDate={today} />
          )}

          {/* Read-only user message */}
          {user.role === Role.READ_ONLY && (
            <div className='mb-8 p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg'>
              <div className='flex items-start'>
                <InformationCircleIcon className='h-6 w-6 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0' />
                <div>
                  <h3 className='text-lg font-medium text-blue-800 dark:text-blue-200 mb-2'>
                    閲覧専用アカウント
                  </h3>
                  <p className='text-blue-700 dark:text-blue-300'>
                    このアカウントではデータの閲覧のみ可能です。案件の作成や編集を行う場合は、管理者にお問い合わせください。
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Demo Guidance Modal */}
      <DemoGuidance onClose={handleCloseDemoGuidance} onStartScenario={handleStartScenario} />
    </div>
  );
};

export default DashboardPage;
